import React, {useState} from "react";
import moment from "moment";
import {toast} from "react-toastify";
import {
    Box, Button, IconButton, InputBase, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Delete02Icon, PencilEdit01Icon, Search01Icon} from "@hugeicons/core-free-icons";
import useConfirm from "../../hooks/useConfirm";
import {LanguageRepository} from "../../repositories/LanguageRepository";
import {useCatalog} from "../../context/CatalogContext";
import {LanguageDialog} from "./components/language-dialog";
import {controlSx, Panel} from "../registry-project/dashboard/components/Panel";

const getErrorMessage = (e, fallback) => e?.response?.data?.message || fallback;

const headCellSx = {
    height: 40, py: 0.5, px: 1.5, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0",
    bgcolor: "#FAFAFD", borderBottom: "1px solid #ECECF3", whiteSpace: "nowrap",
};
const cellSx = {py: 1.25, px: 1.5, fontSize: 13, color: "#23264D", borderBottom: "1px solid #F2F2F7"};
const iconButtonSx = {borderRadius: "50% !important", color: "#8A8AA0", "&:hover": {bgcolor: "#F4F4FA", color: "#4A4A63"}};
const deleteIconButtonSx = {...iconButtonSx, "&:hover": {bgcolor: "rgba(220, 38, 38, 0.08)", color: "#DC2626"}};
const headerButtonSx = {
    height: 38, px: 1.75, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 700,
    color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
};

const CodePill = ({code}) => (
    <Box component={"span"} sx={{
        px: 0.9, py: 0.25, borderRadius: "999px", fontSize: 11, fontWeight: 700, letterSpacing: "0.02em",
        color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)",
    }}>
        {code || "—"}
    </Box>
);

const COLUMNS = 5;

// Admin catalog of languages available to projects and transcribe models.
export const RegistryLanguagesPage = () => {
    const {items: languages, loading, setItems: setLanguages} = useCatalog("languages", {refreshOnMount: true});
    const [openModal, setOpenModal] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [search, setSearch] = useState("");

    const confirmDelete = useConfirm(
        "Are you sure you want to delete this language?",
        "Projects and transcribe models that use this language may stop working."
    );

    const query = search.trim().toLowerCase();
    const filteredLanguages = query
        ? languages.filter(language => [language.language, language.code, language.lang]
            .some(value => String(value || "").toLowerCase().includes(query)))
        : languages;

    const handleOpenModal = (language = null) => {
        setSelectedLanguage(language);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedLanguage(null);
    };

    const handleSave = async (language) => {
        try {
            if (selectedLanguage?.id) {
                const response = await LanguageRepository.edit(selectedLanguage.id, language);
                setLanguages(prev => prev.map(l => l.id === selectedLanguage.id ? response.data : l));
            } else {
                const response = await LanguageRepository.create(language);
                setLanguages(prev => [...prev, response.data]);
            }
            toast.success("Language saved successfully");
            return true;
        } catch (e) {
            console.error(e);
            toast.error(getErrorMessage(e, "Failed to save language"));
            return false;
        }
    };

    const handleDelete = async (language) => {
        const confirmed = await confirmDelete({title: `Delete "${language.language}"?`});
        if (!confirmed) return;
        try {
            await LanguageRepository.delete(language.id);
            setLanguages(prev => prev.filter(l => l.id !== language.id));
            toast.success("Language deleted successfully");
        } catch (e) {
            console.error(e);
            toast.error(getErrorMessage(e, "Failed to delete language. It may still be in use."));
        }
    };

    const emptyMessage = languages.length === 0 ? "No languages added yet" : `No languages match "${search}"`;

    return (
        <Stack gap={3} sx={{p: {xs: 2, md: 3}, maxWidth: 1200, width: "100%", mx: "auto", boxSizing: "border-box"}}>
            <LanguageDialog
                open={openModal}
                selectedLanguage={selectedLanguage}
                handleClose={handleCloseModal}
                onSubmit={handleSave}
            />

            <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
                <Box>
                    <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>MODELS</Typography>
                    <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
                        Languages
                    </Typography>
                    <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
                        Languages available to projects and transcribe models
                    </Typography>
                </Box>
                <Stack direction={"row"} alignItems={"center"} gap={1.25} flexWrap={"wrap"}>
                    <Box component={"label"} sx={{...controlSx, width: 220}}>
                        <HugeiconsIcon icon={Search01Icon} size={16}/>
                        <InputBase
                            placeholder={"Search languages..."}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            inputProps={{"aria-label": "Search languages"}}
                            sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                        />
                    </Box>
                    <Button startIcon={<HugeiconsIcon icon={Add01Icon} size={17}/>} onClick={() => handleOpenModal()} sx={headerButtonSx}>
                        Add Language
                    </Button>
                </Stack>
            </Stack>

            <Panel sx={{overflow: "hidden"}}>
                <TableContainer sx={{overflowX: "auto"}}>
                    <Table size={"small"} sx={{minWidth: 640}}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{...headCellSx, pl: 2.5}}>LANGUAGE</TableCell>
                                <TableCell sx={headCellSx}>CODE</TableCell>
                                <TableCell sx={headCellSx}>LANG</TableCell>
                                <TableCell sx={headCellSx}>CREATED</TableCell>
                                <TableCell sx={{...headCellSx, pr: 2.5}}/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && languages.length === 0 && Array.from({length: 5}).map((_, index) => (
                                <TableRow key={index}>
                                    <TableCell colSpan={COLUMNS} sx={{...cellSx, px: 2.5}}><Skeleton height={32}/></TableCell>
                                </TableRow>
                            ))}
                            {!loading && filteredLanguages.map(language => (
                                <TableRow key={language.id} hover sx={{"&.MuiTableRow-hover:hover": {bgcolor: "#FAFAFD"}}}>
                                    <TableCell sx={{...cellSx, pl: 2.5, fontWeight: 600}}>{language.language}</TableCell>
                                    <TableCell sx={cellSx}><CodePill code={language.code}/></TableCell>
                                    <TableCell sx={{...cellSx, fontFamily: "monospace", fontSize: 12.5}}>{language.lang || "—"}</TableCell>
                                    <TableCell sx={{...cellSx, color: "#8A8AA0", whiteSpace: "nowrap"}}>
                                        {language.dateCreated ? moment(language.dateCreated).format("DD MMM YYYY") : "—"}
                                    </TableCell>
                                    <TableCell sx={{...cellSx, pr: 2.5, whiteSpace: "nowrap"}}>
                                        <Stack direction={"row"} gap={0.25} justifyContent={"flex-end"}>
                                            <Tooltip title={"Edit"}>
                                                <IconButton size={"small"} sx={iconButtonSx} onClick={() => handleOpenModal(language)}>
                                                    <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={"Delete"}>
                                                <IconButton size={"small"} sx={deleteIconButtonSx} onClick={() => handleDelete(language)}>
                                                    <HugeiconsIcon icon={Delete02Icon} size={16}/>
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!loading && filteredLanguages.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={COLUMNS} sx={{...cellSx, borderBottom: 0, py: 6, textAlign: "center", color: "#8A8AA0"}}>
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Panel>
        </Stack>
    );
};
